import { useState, useEffect, useMemo } from 'react';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import { ProvidedType, ApproachReference } from 'types/approach';

export interface ResolvedType {
  schemaSystemName: string;
  displayName: string;
  description: string | null;
  avatar: string | null;
  isForked: boolean;
}

export interface UseResolvedTypesResult {
  types: ResolvedType[];
  loading: boolean;
}

function formatSystemName(systemName: string): string {
  return systemName
    .replace(/^center_/, '')
    .replaceAll('_', ' ')
    .replaceAll(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Resolves provided types to display metadata. For composite approaches
 * where providedTypes is empty but subApproaches exist, fetches each
 * sub-approach definition and unions their providedTypes.
 */
export function useResolvedTypes(
  providedTypes: ProvidedType[] | undefined | null,
  subApproaches?: ApproachReference[] | undefined | null
): UseResolvedTypesResult {
  const apiClients = useApiClientsOrNull();
  const { impersonationDetails } = useImpersonation();
  const organizationId = impersonationDetails?.organizationId;

  const hasDirectTypes = !!providedTypes && providedTypes.length > 0;
  const hasSubApproaches = !!subApproaches && subApproaches.length > 0;

  // Stabilize dependency: serialize system names so array reference changes don't retrigger
  const typesKey = useMemo(
    () =>
      providedTypes
        ?.map((pt) => `${pt.schemaSystemName}:${pt.isForked ?? false}`)
        .sort()
        .join(',') ?? '',
    [providedTypes]
  );

  const subApproachesKey = useMemo(
    () =>
      subApproaches
        ?.map((sa) => sa.approachSystemName)
        .sort()
        .join(',') ?? '',
    [subApproaches]
  );

  const [types, setTypes] = useState<ResolvedType[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!hasDirectTypes && !hasSubApproaches) {
      setTypes([]);
      setLoading(false);
      return;
    }

    if (!apiClients?.schemaClient || !organizationId) {
      return;
    }

    let cancelled = false;

    const resolve = async () => {
      setLoading(true);

      const effectiveTypes: ProvidedType[] = providedTypes ? [...providedTypes] : [];

      // For composite approaches: fetch sub-approach definitions to get their types
      if (!hasDirectTypes && hasSubApproaches && subApproaches) {
        // The sub-approach definitions are independent reads; Promise.all keeps the declared order.
        const included = subApproaches.filter((sa) => sa.mode === 'include');
        const providedBySubApproach = await Promise.all(
          included.map(async (sa): Promise<ProvidedType[]> => {
            try {
              const response = await apiClients.schemaClient.get(
                `/approaches/${encodeURIComponent(sa.approachSystemName)}`,
                { params: { includeMetrics: false } }
              );
              const subDef = response?.approach?.latestVersion?.definition ??
                response?.approach?.version?.definition ??
                response?.data?.approach?.latestVersion?.definition ??
                response?.data?.approach?.version?.definition;
              return subDef?.providedTypes ?? [];
            } catch {
              // Sub-approach fetch failed — skip
              return [];
            }
          })
        );
        for (const provided of providedBySubApproach) {
          effectiveTypes.push(...provided);
        }
      }

      if (effectiveTypes.length === 0) {
        if (!cancelled) {
          setTypes([]);
          setLoading(false);
        }
        return;
      }

      // Deduplicate by schemaSystemName
      const uniqueMap = new Map<string, ProvidedType>();
      for (const pt of effectiveTypes) {
        if (!uniqueMap.has(pt.schemaSystemName)) {
          uniqueMap.set(pt.schemaSystemName, pt);
        }
      }

      // Each schema is an independent read; Promise.all keeps the deduplicated order.
      const results: ResolvedType[] = await Promise.all(
        [...uniqueMap].map(async ([systemName, pt]): Promise<ResolvedType> => {
          const fallback: ResolvedType = {
            schemaSystemName: systemName,
            displayName: formatSystemName(systemName),
            description: null,
            avatar: null,
            isForked: pt.isForked ?? false,
          };
          try {
            const schemas = await apiClients.schemaClient.get('/schemas', {
              params: { systemName },
            });

            const schemaData = Array.isArray(schemas) ? schemas[0] : schemas?.data?.[0];
            if (!schemaData?.name) return fallback;

            return {
              schemaSystemName: systemName,
              displayName: schemaData.name,
              description: schemaData.description ?? null,
              avatar: schemaData.avatar ?? null,
              isForked: pt.isForked ?? false,
            };
          } catch {
            return fallback;
          }
        })
      );

      if (!cancelled) {
        setTypes(results);
        setLoading(false);
      }
    };

    resolve();

    return () => {
      cancelled = true;
    };
  }, [typesKey, subApproachesKey, hasDirectTypes, hasSubApproaches, apiClients, organizationId]);

  return { types, loading };
}
